import { Breakpoints } from '@dustin-web/microsite-config/src/breakpoints'
import { useState } from 'react'
import { useLayout } from '../hooks'
import { useLayoutEffect } from 'react'

export type SliderType =
  | 'startpage'
  | 'startpageFullWidthFalse'
  | 'single'
  | 'default'
  | 'startpageBanners'
  | 'tiles'
  | 'narrow'
  | 'minicart'

const SliderSettings = () => {
  const { responsive } = useLayout()
  const [dynamicVisibilityGutter, setDynamicVisibilityGutter] = useState(70)

  const PRODUCT_CARD_PARTIAL = 0.3
  const PRODUCT_CARD_PARTIAL_LARGER = 0.4
  const PADDING = 16
  let value = 70

  // for some sliders we need to calculate the partialVisibilityGutter dynamically on below SM to preserve correct-ish width of product cards
  // setting a percentage value is not possible
  useLayoutEffect(() => {
    if (responsive.windowSize.width <= 400) {
      value = 70
    } else if (responsive.windowSize.width > 400 && responsive.windowSize.width <= 481) {
      value = (responsive.windowSize.width - PADDING * 2) * PRODUCT_CARD_PARTIAL
    } else {
      value = (responsive.windowSize.width - PADDING * 2) * PRODUCT_CARD_PARTIAL_LARGER
    }

    setDynamicVisibilityGutter(Math.round(value))
  }, [responsive])

  //please always name the settings with the following: mobile, sm, md, lg, xl
  return {
    startpage: {
      mobile: {
        breakpoint: { min: 0, max: Breakpoints.SM },
        items: 1,
        partialVisibilityGutter: dynamicVisibilityGutter,
      },
      sm: {
        breakpoint: { min: Breakpoints.SM, max: Breakpoints.MD },
        items: 2,
        partialVisibilityGutter: 50,
      },
      md: {
        breakpoint: { min: Breakpoints.MD, max: Breakpoints.LG },
        items: 3,
        partialVisibilityGutter: 30,
      },
      lg: {
        breakpoint: { min: Breakpoints.LG, max: Breakpoints.XL },
        items: 4,
      },
      xl: {
        breakpoint: { min: Breakpoints.XL, max: 9999 },
        items: 4,
      },
    },
    startpageFullWidthFalse: {
      mobile: {
        breakpoint: { min: 0, max: 500 },
        items: 1,
        partialVisibilityGutter: 113,
      },
      sm: {
        breakpoint: { min: 500, max: Breakpoints.MD },
        items: 2,
        partialVisibilityGutter: 70,
      },
      md: {
        breakpoint: { min: Breakpoints.MD, max: Breakpoints.LG },
        items: 2,
        partialVisibilityGutter: 70,
      },
      lg: {
        breakpoint: { min: Breakpoints.LG, max: Breakpoints.XL },
        items: 2,
        partialVisibilityGutter: 76,
      },
      xl: {
        breakpoint: { min: Breakpoints.XL, max: 9999 },
        items: 2,
        partialVisibilityGutter: 94,
      },
    },
    single: {
      mobile: {
        breakpoint: { min: 0, max: Breakpoints.SM },
        items: 1,
      },
      sm: {
        breakpoint: { min: Breakpoints.SM, max: Breakpoints.MD },
        items: 1,
      },
      md: {
        breakpoint: { min: Breakpoints.MD, max: Breakpoints.LG },
        items: 1,
      },
      lg: {
        breakpoint: { min: Breakpoints.LG, max: Breakpoints.XL },
        items: 1,
      },
      xl: {
        breakpoint: { min: Breakpoints.XL, max: 9999 },
        items: 1,
      },
    },
    default: {
      mobile: {
        breakpoint: { min: 0, max: Breakpoints.SM },
        items: 1,
        partialVisiblityGutter: dynamicVisibilityGutter,
      },
      sm: {
        breakpoint: { min: Breakpoints.SM, max: Breakpoints.MD },
        items: 2,
      },
      md: {
        breakpoint: { min: Breakpoints.MD, max: Breakpoints.LG },
        items: 3,
      },
      lg: {
        breakpoint: { min: Breakpoints.LG, max: Breakpoints.XL },
        items: 4,
      },
      xl: {
        breakpoint: { min: Breakpoints.XL, max: 9999 },
        items: 4,
      },
    },
    startpageBanners: {
      mobile: {
        breakpoint: { min: 0, max: Breakpoints.SM },
        items: 1,
      },
      sm: {
        breakpoint: { min: Breakpoints.SM, max: Breakpoints.MD },
        items: 1,
      },
      md: {
        breakpoint: { min: Breakpoints.MD, max: Breakpoints.LG },
        items: 1,
      },
      lg: {
        breakpoint: { min: Breakpoints.LG, max: Breakpoints.XL },
        items: 1,
      },
      xl: {
        breakpoint: { min: Breakpoints.XL, max: 9999 },
        items: 1,
      },
    },
    tiles: {
      mobile: {
        breakpoint: { min: 0, max: Breakpoints.SM },
        items: 1.6,
      },
      sm: {
        breakpoint: { min: Breakpoints.SM, max: Breakpoints.MD },
        items: 3.5,
      },
      md: {
        breakpoint: { min: Breakpoints.MD, max: Breakpoints.LG },
        items: 3.5,
      },
      lg: {
        breakpoint: { min: Breakpoints.LG, max: Breakpoints.XL },
        items: 6,
      },
      xl: {
        breakpoint: { min: Breakpoints.XL, max: 9999 },
        items: 6,
      },
    },
    narrow: {
      mobile: {
        breakpoint: { min: 0, max: Breakpoints.SM },
        items: 1,
        partialVisibilityGutter: dynamicVisibilityGutter,
      },
      sm: {
        breakpoint: { min: Breakpoints.SM, max: Breakpoints.MD },
        items: 2,
        partialVisibilityGutter: 50,
      },
      md: {
        breakpoint: { min: Breakpoints.MD, max: Breakpoints.LG },
        items: 3,
        partialVisibilityGutter: 30,
      },
      lg: {
        breakpoint: { min: Breakpoints.LG, max: Breakpoints.XL },
        items: 3,
      },
      xl: {
        breakpoint: { min: Breakpoints.XL, max: 9999 },
        items: 3,
      },
    },
    minicart: {
      // only visible in LG and XL
      lg: {
        breakpoint: { min: Breakpoints.LG, max: Breakpoints.XL },
        items: 2,
        partialVisibilityGutter: 16,
      },
      xl: {
        breakpoint: { min: Breakpoints.XL, max: 9999 },
        items: 2,
        partialVisibilityGutter: 16,
      },
    },
  }
}

export default SliderSettings
